import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useStore } from '../context/StoreContext';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Upload, 
  FileSpreadsheet, 
  AlertTriangle, 
  CheckCircle, 
  Loader2,
  Trash2
} from 'lucide-react';
import { format } from 'date-fns';

const API = process.env.REACT_APP_BACKEND_URL;

export const BankStatementsPage = () => {
  const { stores, selectedStore, setSelectedStore } = useStore();
  const { isAdmin } = useAuth();
  
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankId, setSelectedBankId] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Get selected bank account details
  const selectedBank = bankAccounts.find(b => b.id === selectedBankId);
  
  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [selectedDuplicates, setSelectedDuplicates] = useState([]);
  
  // New bank account dialog - REMOVED: Now fetched from Store Setup

  useEffect(() => {
    if (selectedStore?.id) {
      setSelectedStoreId(selectedStore.id);
    }
  }, [selectedStore]);

  // Handle store selection change - also update global context
  const handleStoreChange = (storeId) => {
    setSelectedStoreId(storeId);
    // Update global store context so header reflects the change
    const store = stores.find(s => s.id === storeId);
    if (store) {
      setSelectedStore(store);
    }
  };

  useEffect(() => {
    if (selectedStoreId) {
      fetchBankAccounts();
      fetchTransactions();
    }
  }, [selectedStoreId]);

  useEffect(() => {
    if (selectedBankId) {
      // Clear transactions immediately to show loading state
      setTransactions([]);
      fetchTransactions();
    } else {
      setTransactions([]);
    }
  }, [selectedBankId]);

  const fetchBankAccounts = () => {
    // Fetch bank accounts from the store setup configuration
    const currentStore = stores.find(s => s.id === selectedStoreId);
    if (currentStore?.bank_accounts && currentStore.bank_accounts.length > 0) {
      // Map store bank accounts to the format expected by the component
      // Include balance from opening_balances
      const openingBalances = currentStore.opening_balances || {};
      
      const storeAccounts = currentStore.bank_accounts.map((acc, idx) => {
        const accId = acc.id || `bank_${idx}`;
        // Look for balance in opening_balances.bank_accounts array or direct mapping
        let balance = 0;
        if (openingBalances.bank_accounts) {
          const balanceEntry = openingBalances.bank_accounts.find(b => b.id === accId || b.bank_name === acc.bank_name);
          balance = balanceEntry?.balance || 0;
        }
        // Also check if balance is directly on the account
        if (acc.balance !== undefined) {
          balance = acc.balance;
        }
        
        return {
          id: accId,
          bank_name: acc.bank_name || acc.name || 'Unknown Bank',
          account_name: acc.account_name || '',
          account_number: acc.account_number || '',
          balance: balance
        };
      });
      setBankAccounts(storeAccounts);
      if (storeAccounts.length > 0 && !selectedBankId) {
        setSelectedBankId(storeAccounts[0].id);
      }
    } else {
      setBankAccounts([]);
      setSelectedBankId('');
    }
  };

  const fetchTransactions = async () => {
    if (!selectedStoreId) return;
    setLoading(true);
    try {
      const params = { store_id: selectedStoreId };
      // Filter by bank account when selected
      if (selectedBankId) {
        params.bank_account_id = selectedBankId;
      }
      const response = await axios.get(`${API}/api/reconciliation/bank-transactions`, {
        params
      });
      setTransactions(response.data);
    } catch (error) {
      console.error('Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!selectedStoreId) {
      toast.error('Please select a store first');
      return;
    }
    
    if (!selectedBankId) {
      toast.error('Please select or add a bank account first');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('store_id', selectedStoreId);
      formData.append('bank_account_id', selectedBankId);
      
      const response = await axios.post(`${API}/api/reconciliation/upload-statement`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setUploadResult(response.data);
      
      // Check for duplicates
      if (response.data.duplicates?.length > 0) {
        setSelectedDuplicates([]);
        setDuplicateDialogOpen(true);
      } else if (response.data.new_transactions?.length > 0) {
        // No duplicates, save directly
        await saveTransactions(response.data.new_transactions, response.data.filename);
      } else {
        toast.info('No new transactions found in file');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload statement');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const saveTransactions = async (txns, filename) => {
    try {
      await axios.post(`${API}/api/reconciliation/save-transactions`, {
        store_id: selectedStoreId,
        bank_account_id: selectedBankId,
        transactions: txns,
        filename: filename
      });
      toast.success(`${txns.length} transactions imported successfully`);
      fetchTransactions();
      setUploadResult(null);
    } catch (error) {
      toast.error('Failed to save transactions');
    }
  };

  const handleDuplicateConfirm = async (action) => {
    if (!uploadResult) return;
    
    let transactionsToSave = [...uploadResult.new_transactions];
    
    if (action === 'include' && selectedDuplicates.length > 0) {
      // Add selected duplicates
      const duplicatesToAdd = uploadResult.duplicates.filter((_, idx) => 
        selectedDuplicates.includes(idx)
      );
      transactionsToSave = [...transactionsToSave, ...duplicatesToAdd];
    }
    
    if (transactionsToSave.length > 0) {
      await saveTransactions(transactionsToSave, uploadResult.filename);
    } else {
      toast.info('No transactions to import');
    }
    
    setDuplicateDialogOpen(false);
    setUploadResult(null);
    setSelectedDuplicates([]);
  };

  const handleDeleteTransaction = async (transactionId) => {
    if (!window.confirm('Are you sure you want to delete this transaction?')) return;
    
    try {
      await axios.delete(`${API}/api/reconciliation/bank-transactions/${transactionId}`);
      toast.success('Transaction deleted');
      fetchTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete transaction');
    }
  };

  const handleClearAllTransactions = async () => {
    if (!selectedBankId) return;
    
    const bankName = bankAccounts.find(b => b.id === selectedBankId)?.bank_name || 'this bank';
    if (!window.confirm(`Are you sure you want to delete ALL non-reconciled transactions for ${bankName}? This cannot be undone.`)) return;
    
    try {
      const response = await axios.delete(`${API}/api/reconciliation/bank-transactions/bulk/delete`, {
        data: {
          bank_account_id: selectedBankId,
          delete_all_for_bank: true
        }
      });
      toast.success(`${response.data.deleted_count} transactions deleted`);
      fetchTransactions();
    } catch (error) {
      toast.error('Failed to delete transactions');
    }
  };

  const handleRecleanDescriptions = async () => {
    if (!selectedBankId) return;
    
    try {
      const response = await axios.post(`${API}/api/reconciliation/bank-transactions/reclean-descriptions`, {
        store_id: selectedStoreId,
        bank_account_id: selectedBankId
      });
      toast.success(`${response.data.updated_count} transaction descriptions cleaned`);
      fetchTransactions();
    } catch (error) {
      toast.error('Failed to clean descriptions');
    }
  };

  const toggleDuplicate = (idx) => {
    setSelectedDuplicates(prev => 
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
            Bank Statements
          </h1>
          <p className="text-muted-foreground mt-1">
            Upload and manage bank statement transactions
          </p>
        </div>
      </div>

      {/* Store & Bank Selection */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Store</Label>
              <Select value={selectedStoreId} onValueChange={handleStoreChange}>
                <SelectTrigger data-testid="bank-store-select">
                  <SelectValue placeholder="Select store" />
                </SelectTrigger>
                <SelectContent>
                  {stores.map(store => (
                    <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Bank Account</Label>
              <Select value={selectedBankId} onValueChange={setSelectedBankId}>
                <SelectTrigger data-testid="bank-account-select">
                  <SelectValue placeholder={bankAccounts.length === 0 ? "No bank accounts configured" : "Select bank account"} />
                </SelectTrigger>
                <SelectContent>
                  {bankAccounts.map(account => (
                    <SelectItem key={account.id} value={account.id}>
                      {account.bank_name} {account.account_name ? `- ${account.account_name}` : ''} {account.account_number ? `(${account.account_number})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {bankAccounts.length === 0 && selectedStoreId && (
                <p className="text-xs text-amber-500">Configure bank accounts in Store Setup</p>
              )}
            </div>

            {/* Bank Balance Display */}
            {selectedBank && (
              <div className="space-y-2">
                <Label>Account Balance</Label>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className={`text-2xl font-bold ${(selectedBank.balance || 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                    £{(selectedBank.balance || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs text-muted-foreground">Current Balance</p>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Upload CSV</Label>
              <div className="relative">
                <Input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  disabled={uploading || !selectedBankId}
                  className="cursor-pointer"
                  data-testid="csv-upload-input"
                />
                {uploading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-background/80">
                    <Loader2 className="h-5 w-5 animate-spin" />
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Supports HSBC, Natwest, Starling, Metro Bank
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transactions Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Bank Transactions
            {selectedBankId && bankAccounts.length > 0 && (
              <span className="text-sm font-normal bg-primary/10 text-primary px-2 py-1 rounded ml-2">
                {bankAccounts.find(b => b.id === selectedBankId)?.bank_name || 'Unknown Bank'}
              </span>
            )}
            <span className="text-sm font-normal text-muted-foreground ml-2">
              ({transactions.length} transactions)
            </span>
          </CardTitle>
          {transactions.length > 0 && (
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="text-blue-500 hover:text-blue-600 hover:bg-blue-500/10"
                onClick={handleRecleanDescriptions}
                data-testid="reclean-descriptions-btn"
              >
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Clean References
              </Button>
              {transactions.some(t => !t.is_reconciled) && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                  onClick={handleClearAllTransactions}
                  data-testid="clear-all-transactions-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Clear All
                </Button>
              )}
            </div>
          )}
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Upload className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No transactions uploaded yet</p>
              <p className="text-sm">Upload a bank statement CSV to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left p-3 font-medium text-muted-foreground">Date</th>
                    <th className="text-left p-3 font-medium text-muted-foreground">Description</th>
                    <th className="text-left p-3 font-medium text-muted-foreground">Type</th>
                    <th className="text-right p-3 font-medium text-muted-foreground">Amount</th>
                    <th className="text-right p-3 font-medium text-muted-foreground">Balance</th>
                    <th className="text-center p-3 font-medium text-muted-foreground">Status</th>
                    <th className="text-center p-3 font-medium text-muted-foreground w-16">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((txn, idx) => (
                    <tr key={txn.id || idx} className="border-b border-border/50 hover:bg-muted/20">
                      <td className="p-3">{txn.date}</td>
                      <td className="p-3 max-w-xs truncate" title={txn.description}>
                        {txn.description}
                      </td>
                      <td className="p-3">
                        <span className="text-xs bg-muted px-2 py-1 rounded">{txn.type}</span>
                      </td>
                      <td className={`p-3 text-right font-mono ${txn.amount >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {txn.amount >= 0 ? '+' : ''}£{Math.abs(txn.amount).toFixed(2)}
                      </td>
                      <td className="p-3 text-right font-mono">£{txn.balance?.toFixed(2)}</td>
                      <td className="p-3 text-center">
                        {txn.is_reconciled ? (
                          <span className="inline-flex items-center gap-1 text-xs bg-green-500/20 text-green-500 px-2 py-1 rounded">
                            <CheckCircle className="h-3 w-3" />
                            Reconciled
                          </span>
                        ) : (
                          <span className="text-xs bg-amber-500/20 text-amber-500 px-2 py-1 rounded">
                            Pending
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        {!txn.is_reconciled && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-red-500 hover:text-red-600 hover:bg-red-500/10"
                            onClick={() => handleDeleteTransaction(txn.id)}
                            data-testid={`delete-txn-${idx}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Duplicate Detection Dialog */}
      <Dialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-500">
              <AlertTriangle className="h-5 w-5" />
              Duplicate Transactions Found
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 space-y-4 overflow-hidden">
            <p className="text-sm text-muted-foreground">
              {uploadResult?.duplicates?.length} transactions already exist in the database. 
              Select any you want to import anyway:
            </p>
            <div className="flex-1 overflow-y-auto border rounded-lg" style={{ maxHeight: '400px' }}>
              <table className="w-full text-sm">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="p-2 text-left w-10"></th>
                    <th className="p-2 text-left">Date</th>
                    <th className="p-2 text-left">Description</th>
                    <th className="p-2 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {uploadResult?.duplicates?.map((dup, idx) => (
                    <tr 
                      key={idx}
                      className={`border-b last:border-b-0 cursor-pointer hover:bg-muted/50 ${
                        selectedDuplicates.includes(idx) ? 'bg-amber-500/10' : ''
                      }`}
                      onClick={() => toggleDuplicate(idx)}
                    >
                      <td className="p-2">
                        <Checkbox 
                          checked={selectedDuplicates.includes(idx)}
                          onCheckedChange={() => toggleDuplicate(idx)}
                        />
                      </td>
                      <td className="p-2 whitespace-nowrap">{dup.date}</td>
                      <td className="p-2">{dup.description}</td>
                      <td className={`p-2 text-right font-mono whitespace-nowrap ${dup.amount >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {dup.amount >= 0 ? '+' : '-'}£{Math.abs(dup.amount).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="bg-muted/30 p-3 rounded-lg text-sm">
              <strong>New transactions to import:</strong> {uploadResult?.new_transactions?.length || 0}
              {selectedDuplicates.length > 0 && (
                <span className="ml-2 text-amber-500">
                  + {selectedDuplicates.length} selected duplicates
                </span>
              )}
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDuplicateDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="outline" onClick={() => handleDuplicateConfirm('skip')}>
              Skip Duplicates
            </Button>
            <Button onClick={() => handleDuplicateConfirm('include')}>
              Import Selected
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BankStatementsPage;
